import React, { useEffect, useMemo, useState } from 'react'
import { Form, Modal } from 'semantic-ui-react'
import { FlexRowBetween, TitleText } from 'components'
import { AppButton } from 'elements'
import { X } from 'phosphor-react'
import { ConsolidatedReportScheduleService } from 'utility'
import {
    extractApiError,
    formatUtcInTimezone,
} from './consolidated-report-email-utils'
import { ModalKv, SkeletonLine } from './consolidated-report-email-styles'

const MIN_REASON = 5
const MAX_REASON = 500

export const FireNowModal = ({
    open,
    onClose,
    onConfirm,
    loading,
    schedule,
    timezone,
    error: externalError,
}) => {
    const [reason, setReason] = useState('')
    const [preview, setPreview] = useState(null)
    const [previewLoading, setPreviewLoading] = useState(false)
    const [error, setError] = useState('')

    useEffect(() => {
        if (!open) {
            setReason('')
            setPreview(null)
            setError('')
            return
        }
        if (!schedule?.ScheduleId) return

        const load = async () => {
            try {
                setPreviewLoading(true)
                const fresh = await ConsolidatedReportScheduleService.get(
                    schedule.ScheduleId,
                )
                setPreview(fresh?.Schedule || fresh)
            } catch (err) {
                setError(extractApiError(err) || 'Could not load preview')
            } finally {
                setPreviewLoading(false)
            }
        }
        load()
    }, [open, schedule?.ScheduleId])

    const reasonValid = useMemo(() => {
        const trimmed = reason.trim()
        return trimmed.length >= MIN_REASON && trimmed.length <= MAX_REASON
    }, [reason])

    const reasonError = useMemo(() => {
        const trimmed = reason.trim()
        if (!trimmed) return ''
        if (trimmed.length < MIN_REASON) return 'Minimum 5 characters required'
        if (trimmed.length > MAX_REASON) return 'Maximum 500 characters'
        return ''
    }, [reason])

    const displayError = externalError || error

    const nextFireLabel = schedule?.NextRunUtc
        ? formatUtcInTimezone(schedule.NextRunUtc, timezone)
        : 'its regular time'

    const handleConfirm = () => {
        if (!reasonValid) return
        onConfirm(reason.trim())
    }

    return (
        <Modal open={open} onClose={loading ? undefined : onClose} size="small">
            <Modal.Header>
                <FlexRowBetween>
                    <TitleText>🚨 Fire Now — send to real recipients</TitleText>
                    <X
                        size={20}
                        style={{ cursor: loading ? 'default' : 'pointer' }}
                        onClick={loading ? undefined : onClose}
                    />
                </FlexRowBetween>
            </Modal.Header>
            <Modal.Content>
                {displayError && (
                    <div className="error-text mb-2">{displayError}</div>
                )}
                <div
                    style={{
                        padding: 12,
                        background: '#fef2f2',
                        border: '1px solid #fecaca',
                        borderRadius: 6,
                        color: '#7f1d1d',
                        fontSize: 12.5,
                        marginBottom: 16,
                    }}
                >
                    <strong>
                        You are about to send a live digest email right now to
                        all active recipients of this schedule.
                    </strong>{' '}
                    The natural scheduled fire ({nextFireLabel}) will still run
                    at its regular time — Fire Now is additive.
                </div>

                <h4 style={{ margin: '0 0 8px', fontSize: 11, color: '#6b7280' }}>
                    THIS WILL SEND
                </h4>
                {previewLoading ? (
                    <>
                        {[1, 2, 3, 4, 5].map(i => (
                            <SkeletonLine key={i} $width="70%" style={{ marginBottom: 8 }} />
                        ))}
                    </>
                ) : (
                    <>
                        <ModalKv>
                            <span className="k">Schedule</span>
                            <span className="v">
                                {preview?.ScheduleName || schedule?.ScheduleName}
                            </span>
                        </ModalKv>
                        <ModalKv>
                            <span className="k">Window</span>
                            <span className="v">
                                {preview?.FireNowWindowLabel || 'Since last fire'}
                            </span>
                        </ModalKv>
                        <ModalKv>
                            <span className="k">Templates in scope</span>
                            <span className="v">
                                {preview?.IncludeAllTemplates
                                    ? `All (${preview?.TemplateCount || 0})`
                                    : `${preview?.TemplateIds?.length || 0} selected`}
                            </span>
                        </ModalKv>
                        <ModalKv>
                            <span className="k">Auditors in scope</span>
                            <span className="v">
                                {preview?.IncludeAllAuditors ? 'All' : 'Selected'}
                            </span>
                        </ModalKv>
                        <ModalKv>
                            <span className="k">Recipients</span>
                            <span className="v">
                                {preview?.RecipientCount ?? schedule?.RecipientCount ?? 0}{' '}
                                total
                            </span>
                        </ModalKv>
                        <ModalKv>
                            <span className="k">Estimated reports</span>
                            <span className="v">
                                ~{preview?.EstimatedReports ?? '—'}
                            </span>
                        </ModalKv>
                    </>
                )}

                <Form style={{ marginTop: 16 }}>
                    <Form.Field error={Boolean(reasonError)}>
                        <label
                            style={{
                                fontSize: 11,
                                color: '#6b7280',
                                textTransform: 'uppercase',
                                letterSpacing: '0.04em',
                            }}
                        >
                            Reason (required)
                        </label>
                        <Form.TextArea
                            rows={3}
                            placeholder="Why is this needed right now? (min 5 chars, saved to audit trail)"
                            value={reason}
                            onChange={(_, { value }) => setReason(value)}
                            disabled={loading}
                        />
                        {reasonError && (
                            <div className="field-error">{reasonError}</div>
                        )}
                    </Form.Field>
                </Form>
            </Modal.Content>
            <Modal.Actions>
                <AppButton onClick={onClose} disabled={loading}>
                    Cancel
                </AppButton>
                <AppButton
                    negative
                    onClick={handleConfirm}
                    loading={loading}
                    disabled={!reasonValid}
                >
                    🚨 Fire Now
                </AppButton>
            </Modal.Actions>
        </Modal>
    )
}
